import { Controller } from "@hotwired/stimulus"

// Drives the field inspection screen: a damage diagram drawn over the
// vehicle's reference image, with tap-to-mark and freehand drawing.
// Annotations are stored normalized (0..1) so they survive any canvas size.
export default class extends Controller {
  static targets = [
    "canvas", "annotationsInput", "markerList", "severityButton", "modeButton",
    "diagramField", "completionAnnotationsField", "emptyHint"
  ]

  static values = {
    imageUrl: String,
    annotations: Array,
    readonly: { type: Boolean, default: false }
  }

  connect() {
    this.annotations = this.annotationsValue || []
    this.severity = "minor"
    this.mode = "marker"
    this.drawing = false
    this.currentPath = null

    this.image = new Image()
    this.image.crossOrigin = "anonymous"
    this.image.onload = () => this.resizeAndRender()
    this.image.onerror = () => this.renderFallback()
    if (this.imageUrlValue) {
      this.image.src = this.imageUrlValue
    } else {
      this.renderFallback()
    }

    this.resizeHandler = () => this.resizeAndRender()
    window.addEventListener("resize", this.resizeHandler)
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
  }

  // --- toolbar ---

  setSeverity(event) {
    this.severity = event.currentTarget.dataset.severity
    this.severityButtonTargets.forEach((b) => {
      b.classList.toggle("ring-2", b.dataset.severity === this.severity)
    })
  }

  setMode(event) {
    this.mode = event.currentTarget.dataset.mode
    this.modeButtonTargets.forEach((b) => {
      b.classList.toggle("bg-brand-soft", b.dataset.mode === this.mode)
    })
  }

  undo() {
    if (this.readonlyValue) return
    // Never undo baseline marks carried over from the handoff inspection.
    for (let i = this.annotations.length - 1; i >= 0; i--) {
      if (!this.annotations[i].carried_over) {
        this.annotations.splice(i, 1)
        break
      }
    }
    this.sync()
  }

  clear() {
    if (this.readonlyValue) return
    this.annotations = this.annotations.filter((a) => a.carried_over)
    this.sync()
  }

  // --- pointer handling ---

  pointerDown(event) {
    if (this.readonlyValue) return
    event.preventDefault()
    const point = this.normalizedPoint(event)

    if (this.mode === "draw") {
      this.drawing = true
      this.currentPath = { mode: "path", severity: this.severity, points: [point] }
    } else {
      const label = String(this.nextLabel())
      this.annotations.push({ mode: "marker", label, severity: this.severity, x: point[0], y: point[1] })
      this.sync()
    }
  }

  pointerMove(event) {
    if (!this.drawing || !this.currentPath) return
    event.preventDefault()
    this.currentPath.points.push(this.normalizedPoint(event))
    this.render()
  }

  pointerUp() {
    if (!this.drawing || !this.currentPath) return
    this.drawing = false
    if (this.currentPath.points.length > 1) this.annotations.push(this.currentPath)
    this.currentPath = null
    this.sync()
  }

  // --- completion: flatten the diagram + serialize marks into the sign-off form ---

  prepareCompletion() {
    if (this.hasDiagramFieldTarget) this.diagramFieldTarget.value = this.flatten()
    if (this.hasCompletionAnnotationsFieldTarget) {
      this.completionAnnotationsFieldTarget.value = JSON.stringify(this.annotations)
    }
  }

  flatten() {
    return this.canvasTarget.toDataURL("image/png")
  }

  // --- internals ---

  nextLabel() {
    const labels = this.annotations.filter((a) => a.mode === "marker").length
    return labels + 1
  }

  sync() {
    if (this.hasAnnotationsInputTarget) {
      this.annotationsInputTarget.value = JSON.stringify(this.annotations)
    }
    this.render()
    this.renderMarkerList()
  }

  normalizedPoint(event) {
    const rect = this.canvasTarget.getBoundingClientRect()
    const x = (event.clientX - rect.left) / rect.width
    const y = (event.clientY - rect.top) / rect.height
    return [Math.min(Math.max(x, 0), 1), Math.min(Math.max(y, 0), 1)]
  }

  resizeAndRender() {
    const canvas = this.canvasTarget
    const width = canvas.parentElement.clientWidth
    if (width === 0) return

    const ratio = this.image.naturalWidth ? this.image.naturalHeight / this.image.naturalWidth : 0.6
    canvas.width = width
    canvas.height = Math.round(width * ratio)
    this.sync()
  }

  severityColor(severity) {
    return { minor: "#D97706", moderate: "#DC2626", severe: "#7C3AED" }[severity] || "#DC2626"
  }

  render() {
    const canvas = this.canvasTarget
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    if (this.image.complete && this.image.naturalWidth > 0) {
      ctx.drawImage(this.image, 0, 0, canvas.width, canvas.height)
    } else {
      ctx.fillStyle = "#F9FAFB"
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }

    const all = this.currentPath ? [...this.annotations, this.currentPath] : this.annotations
    all.forEach((a) => this.renderAnnotation(ctx, a, canvas))
  }

  renderAnnotation(ctx, annotation, canvas) {
    const color = annotation.carried_over ? "#6B7280" : this.severityColor(annotation.severity)

    if (annotation.mode === "path") {
      ctx.strokeStyle = color
      ctx.lineWidth = 3
      ctx.lineCap = "round"
      ctx.beginPath()
      annotation.points.forEach(([x, y], i) => {
        const px = x * canvas.width
        const py = y * canvas.height
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
      })
      ctx.stroke()
    } else {
      const px = annotation.x * canvas.width
      const py = annotation.y * canvas.height
      ctx.beginPath()
      ctx.arc(px, py, 14, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
      ctx.strokeStyle = "#FFFFFF"
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.fillStyle = "#FFFFFF"
      ctx.font = "bold 13px system-ui, sans-serif"
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText(annotation.label, px, py)
    }
  }

  renderMarkerList() {
    if (!this.hasMarkerListTarget) return

    const markers = this.annotations.filter((a) => a.mode === "marker")
    if (this.hasEmptyHintTarget) this.emptyHintTarget.classList.toggle("hidden", markers.length > 0)

    this.markerListTarget.innerHTML = markers.map((m) => `
      <li class="flex items-center gap-2 text-sm py-1.5">
        <span class="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold"
              style="background:${m.carried_over ? "#6B7280" : this.severityColor(m.severity)}">${m.label}</span>
        <span class="capitalize font-medium">${m.severity}</span>
        ${m.carried_over ? '<span class="text-xs text-muted">(from handoff)</span>' : ""}
      </li>
    `).join("")
  }

  renderFallback() {
    this.resizeAndRender()
  }
}
